import type { Media } from './media';
// The client mirrors the API's FacilityType enum
// (api/src/modules/locations/schemas/locations.schema.ts) except BAY, TOUR
// and BOX_PRO_DROP_IN — those are not entitlement-mapped / are out of scope
// for the member booking flow, so the client deliberately trails the API by
// design (unreached facility types simply never appear in the client booking
// flow). COACHING_LESSON and FITNESS_LESSON are added here because they are
// 2 of the 5 ledger-eligible service types (FACILITY_TYPE_SERVICE_TYPE_MAP on
// the server) and could not previously reach the client — no earlier plan
// granted them client-side visibility. COACHING_LESSON_30 is added for the
// same reason (it is now one of five ledger-eligible service types).
// SAUNA, SHOWER and MASSAGE_CHAIR were added so members can book them.
export enum FacilityType {
  HITTING_BAY = 'HITTING_BAY',
  GYM = 'GYM',
  CLUB_FITTING = 'CLUB_FITTING',
  RECOVERY_ROOM = 'RECOVERY_ROOM',
  CONFERENCE_ROOM = 'CONFERENCE_ROOM',
  BOX_ASSESSMENT = 'BOX_ASSESSMENT',
  PRIVATE_HITTING_BAY = 'PRIVATE_HITTING_BAY',
  PUTT_VIEW = 'PUTT_VIEW',
  WORKSPACE = 'WORKSPACE',
  COACHING_LESSON = 'COACHING_LESSON',
  COACHING_LESSON_30 = 'COACHING_LESSON_30',
  FITNESS_LESSON = 'FITNESS_LESSON',
  SAUNA = 'SAUNA',
  SHOWER = 'SHOWER',
  MASSAGE_CHAIR = 'MASSAGE_CHAIR',
}

export interface BookingTypeConfigField {
  value?: any;
  enabled: boolean;
}

export interface BookingType {
  id: string;
  uid: string;
  type: FacilityType;
  price: number;
  currency: string;
  minPrice: number;
  maxPrice: number;
  fields?: Record<string, BookingTypeConfigField>;
  planId?: string;
  locationId?: string;
  active: boolean;
  parentId?: string;
  // Mirrors BookingTypeDto.viaEntitlement (D-04) — discovery-time-only flag
  // signalling this entry was surfaced from the member's entitlement ledger
  // rather than a plan-granted BookingType record. Never persisted (D-03).
  viaEntitlement?: boolean;
}

export interface GetAllRequestDto {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: Record<string, any>;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateBookingTypeDto {
  type: FacilityType;
  price: number;
  currency: string;
  minPrice: number;
  maxPrice: number;
  fields?: Record<string, BookingTypeConfigField>;
  planId?: string;
  locationId?: string;
  active: boolean;
  parentId?: string;
}

export interface UpdateBookingTypeDto {
  type?: FacilityType;
  price?: number;
  currency?: string;
  minPrice?: number;
  maxPrice?: number;
  fields?: Record<string, BookingTypeConfigField>;
  planId?: string;
  locationId?: string;
  active?: boolean;
  parentId?: string;
}

// UI-specific types for BookingType page
export interface BookingTypeUI {
  id: string;
  title: string;
  description?: string;
  facilityType: FacilityType;
  price: number;
  currency: string;
  minPrice: number;
  maxPrice: number;
  active: boolean;
  icon?: string;
  fields?: Record<string, BookingTypeConfigField>;
  // Mirrors BookingType.viaEntitlement — set when this tile was surfaced from
  // the member's entitlement ledger rather than a plan-granted BookingType.
  viaEntitlement?: boolean;
  // Joined in by useUIBookingTypes from GET /member-entitlements/me. Only ever
  // present when viaEntitlement is true. This is the member's balance BEFORE
  // this booking — never decremented mid-flow (UI-SPEC Interaction States).
  remainingCount?: number;
}

// Map facility types to user-friendly titles
export const facilityTypeTitles: Record<FacilityType, string> = {
  [FacilityType.HITTING_BAY]: 'Hitting Bay',
  [FacilityType.GYM]: 'Gym',
  [FacilityType.CLUB_FITTING]: 'Club Fitting',
  [FacilityType.RECOVERY_ROOM]: 'Recovery Room',
  [FacilityType.CONFERENCE_ROOM]: 'Conference Room',
  [FacilityType.BOX_ASSESSMENT]: 'Golf Performance Assessment',
  [FacilityType.PRIVATE_HITTING_BAY]: 'Private Hitting Bay',
  [FacilityType.PUTT_VIEW]: 'PuttView',
  [FacilityType.WORKSPACE]: 'Workspace',
  [FacilityType.COACHING_LESSON]: 'Coaching Lesson',
  [FacilityType.COACHING_LESSON_30]: '30 Min Coaching',
  [FacilityType.FITNESS_LESSON]: 'Fitness Lesson',
  [FacilityType.SAUNA]: 'Sauna',
  [FacilityType.SHOWER]: 'Shower',
  [FacilityType.MASSAGE_CHAIR]: 'Massage Chair',
};

// Team member (coach) interface
export interface TeamMember {
  id: string;
  crmUserId: string;
  firstName: string;
  lastName: string;
  email: string;
  userId: string | null;
  priority: number;
  isPrimary: boolean;
}

// Booking type calendar info
export interface BookingTypeCalendar {
  teamMembers: TeamMember[];
  name: string;
  locationId: string;
  franchiseId: string;
  calendarId: string;
  type: FacilityType;
  slotInterval: number;
  slotDuration: number;
  roomDetails: RoomDetail[];
}

// Room detail interface
export interface RoomDetail {
  id: string;
  name: string;
  capacity: number;
  resourceType: string;
}

// Available slot interface
export interface AvailableSlot {
  startTime: string;
  endTime: string;
  available: boolean;
  calendarId: string;
}

// Booking availability request
export interface BookingAvailabilityRequest {
  locationId: string;
  facility: FacilityType;
  teamMemberId?: string;
  roomId?: string;
  bookingDate: string; // mm-dd-yyyy format
  timezone?: string;
  currentTime?: string; // Current time in location timezone (ISO format)
  isReschedule?: boolean;
  rescheduleBookingId?: string;
}

// Booking availability response
export interface BookingAvailabilityResponse {
  locationId: string;
  facility: FacilityType;
  memberId: string;
  bookingDate: string;
  calendarId: string;
  availableSlots: AvailableSlot[];
  bookingTypeCalendar: BookingTypeCalendar;
}

// Coaching session types
export enum CoachingSessionType {
  ELEVATED_COACHING = 'ELEVATED_COACHING',
  STANDARD = 'STANDARD',
}

// Coaching options for different facility types
export interface CoachingOptions {
  showCoaching: boolean;
  coachingType: CoachingSessionType;
  coachingLabel: string;
}

// Get coaching options based on facility type
export const getCoachingOptions = (facility: FacilityType): CoachingOptions => {
  switch (facility) {
    case FacilityType.HITTING_BAY:
    case FacilityType.CLUB_FITTING:
    case FacilityType.BOX_ASSESSMENT:
      return {
        showCoaching: true,
        coachingType: CoachingSessionType.ELEVATED_COACHING,
        coachingLabel: 'Elevated Coaching Session',
      };
    case FacilityType.GYM:
      return {
        showCoaching: true,
        coachingType: CoachingSessionType.STANDARD,
        coachingLabel: 'Gym Instructor',
      };
    default:
      return {
        showCoaching: false,
        coachingType: CoachingSessionType.STANDARD,
        coachingLabel: '',
      };
  }
};

// Booking data for slot selection, checkout, and success page
export interface BookingData {
  bookingType: string;
  location: {
    name: string;
    address: string;
  };
  date: string;
  time: string;
  times: string[];
  coachingType?: string;
  coachName?: string;
  bayName?: string;
  // Originally-requested primary bay's display name (e.g. "Bay 1"). Set ONLY
  // in hitting-bay auto-assign mode (no manual bay pick) so BookingSuccess can
  // detect and disclose a server-side fallback to a different bay.
  requestedBayName?: string;
  requestBoxPro?: boolean;
  bookingId?: string;
  bookingPayload?: {
    locationId: string;
    facility: string;
    teamMemberId: string;
    bookingDate: string;
    calendarId: string;
    startTime: string;
    endTime: string;
    alternateCalendarIds?: string[];
  };
  bookingResult?: any;
  isRescheduled?: boolean;
  // Mirrors BookingTypeUI.viaEntitlement — carried through to BOTH success
  // screens (v1 pages/Booking/BookingSuccess.tsx and v2
  // v2/screens/BookingSuccess.tsx) via location.state, so one interface
  // serves both trees. Populated in v1's buildSuccessState (Plan 05) and
  // v2's goToSuccess (Plan 07).
  viaEntitlement?: boolean;
  // OPTIMISTIC post-booking count (pre-booking count minus 1), computed at the
  // slot screen and never refetched, because ENTITLEMENT_CONSUME is
  // asynchronous (D-08, D-09). The field name says "After" precisely so
  // nobody wires a live balance into it.
  entitlementRemainingAfter?: number;
}

// Pricing breakdown for booking
export interface Pricing {
  fare: number;
  technologyFee: number;
  additionalServices: number;
  tax: number;
}

// Bay selection interface
export interface Bay {
  id: string;
  name: string;
}

// Aggregated hitting bay availability entry (matches HittingBayAvailabilityDto)
export interface HittingBayAvailabilityEntry {
  calendarId: string; // SmartLev calendar ID for this bay
  calendarName: string; // display name
  priority: number; // sort priority (ascending = higher priority, API sorts ascending)
  availability: Record<string, string[]>; // 'YYYY-MM-DD' -> ISO slot strings[]
}

// Slot in the client-side union (built from HittingBayAvailabilityEntry[])
export interface HittingBaySlot {
  startTime: string; // 'HH:MM' local time (derived from ISO string)
  endTime: string; // 'HH:MM' local time
  calendarId: string; // highest-priority bay CRM calendarId
  alternateCalendarIds: string[]; // other bays with same slot, priority order
}

// Sponsorship data for a calendar (booking type)
export interface CalendarSponsorship {
  sponsorName: string;
  sponsorMessage?: string;
  checkinMessage?: string;
  logoUrl?: string;
  logoMedia?: Media;
  isEnabled: boolean;
}

/**
 * What a Community "Book alongside them" tap carries into the booking flow.
 *
 * Community never books on anyone's behalf (BR10) — this is a deep link into
 * the ordinary flow, pre-filled from the item the member tapped. Every field
 * describes the *existing* booking they are joining, not the one they are
 * about to make.
 *
 * Travels `/community` → `/booking-type` → `/booking-slots` in router state.
 * `BookingType` is a waypoint that resolves the club's booking types and then
 * forwards with `replace`, so Back from the slot picker returns to Community.
 */
export interface CommunityBookingHandoff {
  /** The club the existing booking is at. Not necessarily the member's own. */
  locationId: string;
  /** `FacilityType` of the existing booking, used to skip the Book list. */
  facility?: string;
  /** 'YYYY-MM-DD' in the booking's own timezone — the day to open on. */
  date: string;
  /** ISO start of the existing booking, so its slot can be highlighted. */
  startTime: string;
  /** The slot being joined, excluded so the member takes a different bay. */
  excludeBookingId?: string;
  /** Whose booking this is, for the context line on the slot picker. */
  alongsideMemberName?: string;
  /**
   * The bay calendar THEY hold, for a hitting bay.
   *
   * The point of booking alongside someone is to be beside them, so the
   * picker seats this member in an adjacent bay where it can. Absent on feed
   * rows written before the field existed — auto-assign then applies, which
   * is what always happened.
   */
  alongsideCalendarId?: string;
}
